import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { usePreference } from '../../Data/Preference';
import { useSearchContext } from '../Search/Provider';

const SCROLL_SAVE_THROTTLE_MS = 150;
const RESTORE_MAX_ATTEMPTS = 20;
const RESTORE_TIMEOUT_MS = 1000;

const fingerprintSearch = (search) => {
    if (!search) {
        return null;
    }

    return JSON.stringify(search);
};

const clampScrollTop = (element, top) => {
    const maxTop = Math.max(0, element.scrollHeight - element.clientHeight);
    return Math.min(Math.max(0, top), maxTop);
};

const canRestoreScroll = (element, top) => {
    if (top === 0) {
        return true;
    }

    return element.scrollHeight - element.clientHeight >= top;
};

const scrollStateMatches = (current, next) => {
    return current?.top === next.top && current?.search === next.search;
};

const usePersistPageScroll = (scrollRef, persistScrollKey) => {
    const searchContext = useSearchContext();
    const searchFingerprint = searchContext
        ? fingerprintSearch(searchContext.searchAndSortDebounced)
        : null;

    const [savedScroll, setSavedScroll] = usePreference(
        `page-scroll:${persistScrollKey}`,
        null,
        true,
        false,
    );

    const saveTimerRef = useRef(null);
    const restoredRef = useRef(false);
    const restoreAttemptsRef = useRef(0);
    const previousSearchFingerprintRef = useRef(searchFingerprint);
    const savedScrollRef = useRef(savedScroll);
    const setSavedScrollRef = useRef(setSavedScroll);
    const searchFingerprintRef = useRef(searchFingerprint);

    savedScrollRef.current = savedScroll;
    setSavedScrollRef.current = setSavedScroll;
    searchFingerprintRef.current = searchFingerprint;

    const persistScroll = useCallback((next) => {
        if (scrollStateMatches(savedScrollRef.current, next)) {
            return;
        }

        setSavedScrollRef.current(next);
    }, []);

    const saveScroll = useCallback(() => {
        if (!scrollRef.current) {
            return;
        }

        persistScroll({
            top: scrollRef.current.scrollTop,
            search: searchFingerprintRef.current,
        });
    }, [scrollRef, persistScroll]);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element) {
            return;
        }

        const onScroll = () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }

            saveTimerRef.current = setTimeout(saveScroll, SCROLL_SAVE_THROTTLE_MS);
        };

        element.addEventListener('scroll', onScroll);

        return () => {
            element.removeEventListener('scroll', onScroll);

            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
            }

            saveScroll();
        };
    }, [scrollRef, saveScroll]);

    useEffect(() => {
        if (!searchContext) {
            return;
        }

        if (previousSearchFingerprintRef.current === searchFingerprint) {
            return;
        }

        previousSearchFingerprintRef.current = searchFingerprint;
        restoredRef.current = true;

        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }

        persistScroll({
            top: 0,
            search: searchFingerprint,
        });
    }, [searchContext, searchFingerprint, scrollRef, persistScroll]);

    const tryRestore = useCallback(() => {
        if (!scrollRef.current || savedScroll === null || restoredRef.current) {
            return false;
        }

        const savedSearch = savedScroll.search ?? null;
        if (savedSearch !== searchFingerprint) {
            return false;
        }

        const top = savedScroll.top ?? 0;
        if (!canRestoreScroll(scrollRef.current, top)) {
            return false;
        }

        scrollRef.current.scrollTop = clampScrollTop(scrollRef.current, top);
        restoredRef.current = true;
        return true;
    }, [scrollRef, savedScroll, searchFingerprint]);

    useLayoutEffect(() => {
        restoredRef.current = false;
        restoreAttemptsRef.current = 0;
    }, [persistScrollKey]);

    useLayoutEffect(() => {
        if (savedScroll === null) {
            return;
        }

        tryRestore();
    }, [savedScroll, tryRestore]);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element || savedScroll === null) {
            return;
        }

        const deadline = Date.now() + RESTORE_TIMEOUT_MS;

        const observer = new ResizeObserver(() => {
            if (restoredRef.current || restoreAttemptsRef.current >= RESTORE_MAX_ATTEMPTS) {
                return;
            }

            if (Date.now() > deadline) {
                return;
            }

            restoreAttemptsRef.current += 1;
            tryRestore();
        });

        observer.observe(element);

        const interval = setInterval(() => {
            if (restoredRef.current || Date.now() > deadline) {
                clearInterval(interval);
                return;
            }

            restoreAttemptsRef.current += 1;
            tryRestore();
        }, 50);

        return () => {
            observer.disconnect();
            clearInterval(interval);
        };
    }, [scrollRef, savedScroll, tryRestore]);
};

export default usePersistPageScroll;
